// Décrire Mon Projet - Fonctionnalité Star
// Les artisans ne paient PAS les leads (différence majeure avec Travaux.com)
// Design Ultra Premium avec parcours guidé

import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  FileText, ArrowRight, ArrowLeft, CheckCircle2, Wrench, Zap, Droplets,
  Paintbrush, Home, Building2, Hammer, Flame, TreeDeciduous, Shield,
  MapPin, Calendar, Euro, Camera, Upload, Send, Sparkles, Gift, Users,
  Clock, BadgeCheck, Star, Phone, Mail, MessageSquare, ChevronRight,
  Waves, Sun, Wind, Truck, Shovel, HardHat, Fence, Leaf
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import UnifiedHeader from '../components/layout/UnifiedHeader';
import MainNavigation from '../components/layout/MainNavigation';

import { API_URL } from '../config/api';

// Types de travaux
const TYPES_TRAVAUX = [
  { id: 'renovation', label: 'Rénovation', icon: Home, color: '#1E40AF', description: 'Salle de bain, cuisine, appartement...' },
  { id: 'construction', label: 'Construction', icon: Building2, color: '#10B981', description: 'Maison, extension, garage...' },
  { id: 'plomberie', label: 'Plomberie', icon: Droplets, color: '#0EA5E9', description: 'Fuite, installation, chauffe-eau...' },
  { id: 'electricite', label: 'Électricité', icon: Zap, color: '#F59E0B', description: 'Tableau, prises, éclairage...' },
  { id: 'peinture', label: 'Peinture', icon: Paintbrush, color: '#EC4899', description: 'Intérieur, extérieur, décoration...' },
  { id: 'menuiserie', label: 'Menuiserie', icon: Hammer, color: '#8B5CF6', description: 'Portes, fenêtres, escaliers...' },
  { id: 'chauffage', label: 'Chauffage', icon: Flame, color: '#EF4444', description: 'Chaudière, pompe à chaleur, clim...' },
  { id: 'piscine', label: 'Piscine', icon: Waves, color: '#06B6D4', description: 'Construction, rénovation, entretien...' },
  { id: 'solaire', label: 'Solaire', icon: Sun, color: '#FBBF24', description: 'Panneaux, installation, batterie...' },
  { id: 'toiture', label: 'Toiture', icon: HardHat, color: '#78716C', description: 'Couverture, isolation, charpente...' },
  { id: 'maconnerie', label: 'Maçonnerie', icon: Shovel, color: '#A3A3A3', description: 'Murs, fondations, terrasse...' },
  { id: 'jardin', label: 'Jardin', icon: Leaf, color: '#22C55E', description: 'Aménagement, entretien, arrosage...' },
  { id: 'cloture', label: 'Clôture', icon: Fence, color: '#92400E', description: 'Portail, grillage, mur...' },
  { id: 'demenagement', label: 'Déménagement', icon: Truck, color: '#6366F1', description: 'Transport, montage, stockage...' },
  { id: 'climatisation', label: 'Climatisation', icon: Wind, color: '#38BDF8', description: 'Installation, entretien, dépannage...' },
  { id: 'autre', label: 'Autre', icon: Wrench, color: '#71717A', description: 'Autre type de travaux...' },
];

// Urgences
const URGENCES = [
  { id: 'urgent', label: 'Urgent', sublabel: 'Sous 48h', color: '#EF4444' },
  { id: 'rapide', label: 'Rapide', sublabel: 'Sous 1 semaine', color: '#F59E0B' },
  { id: 'normal', label: 'Normal', sublabel: 'Sous 1 mois', color: '#10B981' },
  { id: 'flexible', label: 'Flexible', sublabel: 'Pas de rush', color: '#6366F1' },
];

// Budgets
const BUDGETS = [
  { id: 'small', label: 'Moins de 1 000€', range: '< 1k€', maxValue: 1000 },
  { id: 'medium', label: '1 000€ - 5 000€', range: '1k - 5k€', maxValue: 5000 },
  { id: 'large', label: '5 000€ - 15 000€', range: '5k - 15k€', maxValue: 15000 },
  { id: 'xlarge', label: '15 000€ - 50 000€', range: '15k - 50k€', maxValue: 50000 },
  { id: 'premium', label: 'Plus de 50 000€', range: '> 50k€', maxValue: 100000 },
];

// Composant Step Indicator
function StepIndicator({ currentStep, totalSteps }) {
  return (
    <div className="flex items-center justify-center gap-2 mb-8">
      {Array.from({ length: totalSteps }).map((_, i) => (
        <div key={i} className="flex items-center">
          <div 
            className={`w-10 h-10 rounded-full flex items-center justify-center font-bold transition-all ${
              i + 1 === currentStep 
                ? 'bg-[#F97316] text-white scale-110' 
                : i + 1 < currentStep
                  ? 'bg-emerald-500 text-white'
                  : 'bg-gray-200 text-gray-500'
            }`}
          >
            {i + 1 < currentStep ? <CheckCircle2 size={20} /> : i + 1}
          </div>
          {i < totalSteps - 1 && (
            <div className={`w-12 h-1 mx-1 rounded ${i + 1 < currentStep ? 'bg-emerald-500' : 'bg-gray-200'}`} />
          )}
        </div>
      ))}
    </div>
  );
}

// Composant principal
export default function DecrireProjetPage({ embedded = false, onBack }) {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    type: '',
    description: '',
    urgence: '',
    budget: '',
    ville: '',
    codePostal: '',
    nom: '',
    telephone: '',
    email: '',
    photos: []
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const totalSteps = 5;

  const updateForm = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const nextStep = () => {
    if (step < totalSteps) setStep(step + 1);
  };

  const prevStep = () => {
    if (step > 1) setStep(step - 1);
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      // Préparer les données pour l'API publique
      const requestData = {
        prestation_name: TYPES_TRAVAUX.find(t => t.id === formData.type)?.label || formData.type,
        description: formData.description,
        city: formData.ville,
        postal_code: formData.codePostal,
        phone: formData.telephone,
        client_name: formData.nom,
        client_email: formData.email,
        project_type: formData.type,
        urgency: formData.urgence,
        budget_range: formData.budget,
        estimated_price: BUDGETS.find(b => b.id === formData.budget)?.maxValue || 0
      };

      const response = await fetch(`${API_URL}/api/artisans/devis-requests/public`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestData)
      });

      if (response.ok) {
        const data = await response.json();
        console.log('Demande créée:', data);
        setIsSuccess(true);
        toast.success('Votre demande a été envoyée aux artisans !');
      } else {
        const errorData = await response.json();
        console.error('Erreur API:', errorData);
        toast.error('Erreur lors de l\'envoi de la demande');
      }
    } catch (error) {
      console.error('Erreur:', error);
      toast.error('Erreur de connexion');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Écran de succès
  if (isSuccess) {
    return (
      <div
        className={embedded ? '' : 'min-h-screen bg-[#F8FAFC]'}
        style={embedded ? {
          position: 'fixed', top: 0, left: '288px', right: 0, bottom: 0,
          zIndex: 40, background: '#F8FAFC', overflowY: 'auto'
        } : {}}
        data-testid="decrire-projet-success"
      >
        {/* Barre retour uniquement */}
        <div className="flex items-center px-8 py-4 bg-white border-b border-gray-100 sticky top-0 z-20">
          <button
            onClick={() => onBack ? onBack() : navigate('/artisans')}
            className="flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-600 hover:text-gray-800 rounded-xl font-medium text-sm transition-colors"
          >
            <ArrowLeft size={16} />
            Retour
          </button>
        </div>
        
        <div className="max-w-2xl mx-auto px-4 py-16 text-center">
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ type: 'spring', duration: 0.5 }}
            className="w-24 h-24 mx-auto mb-6 rounded-full bg-emerald-100 flex items-center justify-center"
          >
            <CheckCircle2 size={48} className="text-emerald-500" />
          </motion.div>
          
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-3xl font-bold text-gray-900 mb-4"
          >
            Demande envoyée !
          </motion.h1>
          
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="text-lg text-gray-600 mb-8"
          >
            Les artisans de ta région ont reçu ta demande. 
            <span className="block text-emerald-600 font-semibold mt-2">
              Tu recevras jusqu'à 5 devis gratuits sous 48h !
            </span>
          </motion.p>
          
          {/* Ce qui nous différencie */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="bg-gradient-to-r from-[#1E40AF] to-[#3B82F6] rounded-2xl p-6 text-white mb-8"
          >
            <div className="flex items-center justify-center gap-2 mb-3">
              <Gift size={24} />
              <span className="font-bold text-lg">Ce qui nous différencie</span>
            </div>
            <p className="text-white/90">
              Chez AbracadaBati, <span className="font-bold text-yellow-300">les artisans ne paient pas les leads</span>. 
              Ils peuvent donc te proposer des devis plus compétitifs !
            </p>
          </motion.div>
          
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5 }}
            className="flex flex-col sm:flex-row items-center justify-center gap-4"
          >
            <Link to="/">
              <Button variant="outline" className="rounded-xl h-12 px-6">
                Retour à l'accueil
              </Button>
            </Link>
            <Link to="/artisans">
              <Button className="bg-[#F97316] hover:bg-[#EA580C] rounded-xl h-12 px-6">
                Voir les artisans
                <ArrowRight size={18} className="ml-2" />
              </Button>
            </Link>
          </motion.div>
        </div>
      </div>
    );
  }

  return (
    <div
      className={embedded ? '' : 'min-h-screen bg-[#F8FAFC]'}
      style={embedded ? {
        position: 'fixed', top: 0, left: '288px', right: 0, bottom: 0,
        zIndex: 40, background: '#F8FAFC', overflowY: 'auto'
      } : {}}
      data-testid="decrire-projet-page"
    >

      {/* Barre retour — seul élément de navigation, sticky en haut */}
      <div className="flex items-center px-8 py-4 bg-white border-b border-gray-100 sticky top-0 z-20">
        <button
          onClick={() => onBack ? onBack() : navigate('/artisans')}
          className="flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-600 hover:text-gray-800 rounded-xl font-medium text-sm transition-colors"
        >
          <ArrowLeft size={16} />
          Retour
        </button>
      </div>

      {/* Hero Mobile - Style Premium Orange */}
      <div className="md:hidden bg-gradient-to-r from-[#FFF7ED] via-[#FFEDD5] to-[#FFF7ED] border-t-4 border-b-4 border-[#F97316] relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: 'repeating-linear-gradient(45deg, transparent, transparent 10px, rgba(249,115,22,0.1) 10px, rgba(249,115,22,0.1) 20px)'
          }} />
        </div>
        <div className="px-4 pt-6 pb-3">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-[#C2410C] whitespace-nowrap leading-none">
              Décris ton projet
            </h1>
            <p className="text-black text-sm font-normal leading-none mt-2">
              Reçois jusqu'à <span className="font-semibold text-[#F97316]">5 devis gratuits</span> d'artisans qualifiés
            </p>
          </div>
        </div>
      </div>

      {/* Hero Desktop/Tablet - Design épuré sans fond coloré */}
      <div className="hidden md:block mx-4 lg:mx-8 mt-6 mb-2">
        <div className="max-w-3xl mx-auto text-center">
          <div className="flex items-center justify-center gap-2 mb-3">
            <FileText size={28} className="text-[#F97316]" />
            <h1 className="text-2xl sm:text-3xl font-bold text-[#1E3A5F]">Décris ton projet</h1>
          </div>
          <p className="text-gray-500">
            Reçois jusqu'à <span className="text-[#F97316] font-bold">5 devis gratuits</span> d'artisans qualifiés
          </p>
        </div>
      </div>

      {/* Formulaire */}
      <div className="max-w-3xl md:max-w-5xl mx-auto px-4 py-8">
        <StepIndicator currentStep={step} totalSteps={totalSteps} />

        <div className="bg-white rounded-2xl shadow-xl p-6 sm:p-8">
          <AnimatePresence mode="wait">
            {/* ÉTAPE 1 : Type de travaux */}
            {step === 1 && (
              <motion.div
                key="step1"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
              >
                <h2 className="text-xl font-bold text-gray-900 mb-2">Quel type de travaux ?</h2>
                <p className="text-gray-500 mb-6">Sélectionnez la catégorie principale</p>

                <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-5 gap-3">
                  {TYPES_TRAVAUX.map((type) => {
                    const Icon = type.icon;
                    const isSelected = formData.type === type.id;
                    return (
                      <button
                        key={type.id}
                        onClick={() => updateForm('type', type.id)}
                        className={`p-4 rounded-xl border-2 transition-all text-left ${
                          isSelected 
                            ? 'border-[#1E40AF] bg-[#1E40AF]/5' 
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <div 
                          className="w-10 h-10 rounded-xl flex items-center justify-center mb-2"
                          style={{ backgroundColor: `${type.color}20` }}
                        >
                          <Icon size={22} style={{ color: type.color }} />
                        </div>
                        <p className="font-semibold text-gray-900 text-sm">{type.label}</p>
                        <p className="text-xs text-gray-500 mt-0.5 line-clamp-1">{type.description}</p>
                      </button>
                    );
                  })}
                </div>
              </motion.div>
            )}

            {/* ÉTAPE 2 : Description */}
            {step === 2 && (
              <motion.div
                key="step2"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
              >
                <h2 className="text-xl font-bold text-gray-900 mb-2">Décris ton projet</h2>
                <p className="text-gray-500 mb-6">Plus tu es précis, meilleurs seront les devis</p>
                
                <textarea
                  value={formData.description}
                  onChange={(e) => updateForm('description', e.target.value)}
                  placeholder="Ex: Je souhaite rénover ma salle de bain de 6m². Je veux remplacer la baignoire par une douche à l'italienne, changer le carrelage mural et au sol, et installer un nouveau meuble vasque..."
                  className="w-full h-40 p-4 border border-gray-200 rounded-xl text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#1E40AF]/20 focus:border-[#1E40AF] resize-none"
                />
                
                {/* Tips */}
                <div className="mt-4 p-4 bg-blue-50 rounded-xl">
                  <p className="text-sm font-medium text-blue-800 mb-2">💡 Conseils pour un bon devis :</p>
                  <ul className="text-sm text-blue-700 space-y-1">
                    <li>• Précisez les dimensions (m²)</li>
                    <li>• Mentionnez les matériaux souhaités</li>
                    <li>• Indique si tu fournis le matériel</li>
                  </ul>
                </div>
              </motion.div>
            )}

            {/* ÉTAPE 3 : Urgence et Budget */}
            {step === 3 && (
              <motion.div
                key="step3"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
              >
                <h2 className="text-xl font-bold text-gray-900 mb-6">Urgence et budget</h2>
                
                {/* Urgence */}
                <div className="mb-8">
                  <p className="font-medium text-gray-700 mb-3 flex items-center gap-2">
                    <Clock size={18} />
                    Quand souhaitez-vous commencer ?
                  </p>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    {URGENCES.map((u) => {
                      const isSelected = formData.urgence === u.id;
                      return (
                        <button
                          key={u.id}
                          onClick={() => updateForm('urgence', u.id)}
                          className={`p-4 rounded-xl border-2 transition-all text-center ${
                            isSelected 
                              ? 'border-[#1E40AF] bg-[#1E40AF]/5' 
                              : 'border-gray-200 hover:border-gray-300'
                          }`}
                        >
                          <div 
                            className="w-3 h-3 rounded-full mx-auto mb-2"
                            style={{ backgroundColor: u.color }}
                          />
                          <p className="font-semibold text-gray-900">{u.label}</p>
                          <p className="text-xs text-gray-500">{u.sublabel}</p>
                        </button>
                      );
                    })}
                  </div>
                </div>
                
                {/* Budget */}
                <div>
                  <p className="font-medium text-gray-700 mb-3 flex items-center gap-2">
                    <Euro size={18} />
                    Quel est ton budget estimé ?
                  </p>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                    {BUDGETS.map((b) => {
                      const isSelected = formData.budget === b.id;
                      return (
                        <button
                          key={b.id}
                          onClick={() => updateForm('budget', b.id)}
                          className={`p-4 rounded-xl border-2 transition-all ${
                            isSelected 
                              ? 'border-[#1E40AF] bg-[#1E40AF]/5' 
                              : 'border-gray-200 hover:border-gray-300'
                          }`}
                        >
                          <p className="font-semibold text-gray-900">{b.range}</p>
                          <p className="text-xs text-gray-500">{b.label}</p>
                        </button>
                      );
                    })}
                  </div>
                </div>
              </motion.div>
            )}

            {/* ÉTAPE 4 : Localisation */}
            {step === 4 && (
              <motion.div
                key="step4"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
              >
                <h2 className="text-xl font-bold text-gray-900 mb-2">Où se situe le chantier ?</h2>
                <p className="text-gray-500 mb-6">Pour trouver les artisans les plus proches</p>
                
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <MapPin size={16} className="inline mr-1" />
                      Code postal *
                    </label>
                    <Input
                      type="text"
                      value={formData.codePostal}
                      onChange={(e) => updateForm('codePostal', e.target.value)}
                      placeholder="Ex: 75001"
                      className="h-12 rounded-xl"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Ville *
                    </label>
                    <Input
                      type="text"
                      value={formData.ville}
                      onChange={(e) => updateForm('ville', e.target.value)}
                      placeholder="Ex: Paris"
                      className="h-12 rounded-xl"
                    />
                  </div>
                </div>
                
                {/* Info */}
                <div className="mt-6 p-4 bg-emerald-50 rounded-xl flex items-start gap-3">
                  <Shield size={20} className="text-emerald-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-medium text-emerald-800">Vos données sont protégées</p>
                    <p className="text-sm text-emerald-700">
                      Votre adresse exacte n'est jamais partagée avec les artisans.
                    </p>
                  </div>
                </div>
              </motion.div>
            )}

            {/* ÉTAPE 5 : Coordonnées */}
            {step === 5 && (
              <motion.div
                key="step5"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
              >
                <h2 className="text-xl font-bold text-gray-900 mb-2">Vos coordonnées</h2>
                <p className="text-gray-500 mb-6">Pour recevoir les devis des artisans</p>
                
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Votre nom *
                    </label>
                    <Input
                      type="text"
                      value={formData.nom}
                      onChange={(e) => updateForm('nom', e.target.value)}
                      placeholder="Ex: Jean Dupont"
                      className="h-12 rounded-xl"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <Phone size={16} className="inline mr-1" />
                      Téléphone *
                    </label>
                    <Input
                      type="tel"
                      value={formData.telephone}
                      onChange={(e) => updateForm('telephone', e.target.value)}
                      placeholder="Ex: 06 12 34 56 78"
                      className="h-12 rounded-xl"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <Mail size={16} className="inline mr-1" />
                      Email *
                    </label>
                    <Input
                      type="email"
                      value={formData.email}
                      onChange={(e) => updateForm('email', e.target.value)}
                      placeholder="Ex: jean.dupont@email.com"
                      className="h-12 rounded-xl"
                    />
                  </div>
                </div>

                {/* BANDEAU AVERTISSEMENT DÉMONSTRATION */}
                <div className="mt-6 p-4 bg-amber-50 border-2 border-amber-400 rounded-xl">
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-full bg-amber-500 flex items-center justify-center flex-shrink-0">
                      <span className="text-white font-bold text-sm">!</span>
                    </div>
                    <div>
                      <p className="font-bold text-amber-800 mb-1">Mode Démonstration</p>
                      <p className="text-sm text-amber-700">
                        Tu explores la <span className="font-bold">version bêta</span> d'AbracadaBati. 
                        Cette demande ne sera pas réellement envoyée aux artisans. 
                        Le lancement officiel est prévu le <span className="font-bold text-amber-900">1er juillet 2026</span>.
                      </p>
                    </div>
                  </div>
                </div>
                
                {/* Récap */}
                <div className="mt-6 p-4 bg-gray-50 rounded-xl">
                  <p className="font-medium text-gray-900 mb-2">Récapitulatif</p>
                  <div className="text-sm text-gray-600 space-y-1">
                    <p>• Type : <span className="font-medium">{TYPES_TRAVAUX.find(t => t.id === formData.type)?.label || '-'}</span></p>
                    <p>• Urgence : <span className="font-medium">{URGENCES.find(u => u.id === formData.urgence)?.label || '-'}</span></p>
                    <p>• Budget : <span className="font-medium">{BUDGETS.find(b => b.id === formData.budget)?.label || '-'}</span></p>
                    <p>• Lieu : <span className="font-medium">{formData.codePostal} {formData.ville}</span></p>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Navigation */}
          <div className="flex items-center justify-between mt-8 pt-6 border-t border-gray-100">
            <Button
              variant="outline"
              onClick={prevStep}
              disabled={step === 1}
              className="rounded-xl h-12 px-6"
            >
              <ArrowLeft size={18} className="mr-2" />
              Retour
            </Button>
            
            {step < totalSteps ? (
              <Button
                onClick={nextStep}
                disabled={
                  (step === 1 && !formData.type) ||
                  (step === 2 && !formData.description) ||
                  (step === 3 && (!formData.urgence || !formData.budget)) ||
                  (step === 4 && (!formData.ville || !formData.codePostal))
                }
                className="bg-[#F97316] hover:bg-[#EA580C] rounded-xl h-12 px-6"
              >
                Continuer
                <ArrowRight size={18} className="ml-2" />
              </Button>
            ) : (
              <Button
                onClick={handleSubmit}
                disabled={isSubmitting || !formData.nom || !formData.telephone || !formData.email}
                className="bg-emerald-500 hover:bg-emerald-600 rounded-xl h-12 px-8"
              >
                {isSubmitting ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                    Envoi...
                  </>
                ) : (
                  <>
                    <Send size={18} className="mr-2" />
                    Envoyer ma demande
                  </>
                )}
              </Button>
            )}
          </div>
        </div>

      </div>

      {/* Spacer mobile */}
      <div className="h-20 lg:hidden" />
    </div>
  );
}
